import { logger } from '@/utils/logger/logger';
import * as semver from 'semver';

/**
 * Compare two semantic version strings
 * @param current - Current version string (e.g., "1.2.3")
 * @param target - Target version string to compare against
 * @returns -1 if current < target, 0 if equal, 1 if current > target, null if parsing fails
 */
export function compareVersions(current: string, target: string): -1 | 0 | 1 | null {
  try {
    const currentParsed = semver.coerce(current);
    const targetParsed = semver.coerce(target);

    if (!currentParsed || !targetParsed) {
      logger.warn('Failed to parse version strings', { current, target });
      return null;
    }

    return semver.compare(currentParsed, targetParsed);
  } catch (error) {
    logger.error('Error comparing versions', { error, current, target });
    return null;
  }
}

/**
 * Check if the current version is in the blocked versions list
 * @param current - Current version string
 * @param blockedVersions - Array of blocked version strings
 * @returns true if current version is blocked, false otherwise
 */
export function isVersionBlocked(current: string, blockedVersions: string[]): boolean {
  try {
    const currentParsed = semver.coerce(current);
    if (!currentParsed) {
      logger.warn('Failed to parse current version', { current });
      return false; // Safe default: don't block if we can't parse
    }

    return blockedVersions.some((blockedVersion) => {
      const blockedParsed = semver.coerce(blockedVersion);
      if (!blockedParsed) {
        logger.warn('Failed to parse blocked version', { blockedVersion });
        return false;
      }
      return semver.eq(currentParsed, blockedParsed);
    });
  } catch (error) {
    logger.error('Error checking blocked versions', { error, current, blockedVersions });
    return false; // Safe default: don't block on error
  }
}

/**
 * Check if the current version is below the minimum supported version
 * @param current - Current version string
 * @param minVersion - Minimum supported version string
 * @returns true if current < minVersion, false otherwise
 */
export function isVersionBelowMinimum(current: string, minVersion: string): boolean {
  try {
    const comparison = compareVersions(current, minVersion);
    if (comparison === null) {
      logger.warn('Failed to compare versions for minimum check', {
        current,
        minVersion,
      });
      return false; // Safe default: don't enforce if we can't parse
    }
    return comparison === -1; // current < minVersion
  } catch (error) {
    logger.error('Error checking minimum version', { error, current, minVersion });
    return false; // Safe default: don't enforce on error
  }
}
